import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Copy,
  ExternalLink,
  Info,
  Loader2,
  Plus,
  Save,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { Link } from "react-router";
import {
  useGetCarrierRateBands,
  useAtomicSaveCarrierRateBands,
  type CarrierRateBand,
  type AtomicSaveBandInput,
  formatCents,
} from "@/api/carrier-rate-bands";
import {
  useGetPickupRateBands,
  type PickupRateBand,
} from "@/api/pickup-rate-bands";
import {
  useGetRedistributionRateBands,
  useAtomicSaveRedistRateBands,
  type RedistributionRateBand,
  type AtomicSaveRedistBandInput,
} from "@/api/redistribution-rate-bands";
import {
  useGetCarrierServices,
  useUpdateCarrierService,
  type CarrierServiceListItem,
} from "@/api/carrier-services";
import {
  useListCarrierAddOnServices,
  useListCarrierAddOnServiceGrants,
  useListCarrierAddOnServiceAccess,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import { useGetServiceAreas } from "@/api/service-areas";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { MarkupConfig, type MarkupConfigHandle } from "./platform-fee-config";
import { QuotePreviewPanel } from "./quote-preview-panel";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { failureReason } from "@/lib/api-error";

/**
 * Pricing-merger Phase 8: unified pricing workspace (per-route cards).
 *
 * One scope = (carrierService, pickupService, dispatchCenter, validFrom).
 * Self-rated label pricing is an explicit lane × weight-bracket matrix.
 *
 * Brackets are SHARED across all routes — adding/removing a bracket updates
 * every route's table atomically. The trash icon to remove a bracket only
 * appears on the first route's rows so the action is unambiguous.
 *
 * Empty cells (`isEmpty: true`) render with an "Add rate" button and are
 * skipped at save time. A shipment matching that lane/bracket receives no
 * carrier option; label pricing has no wildcard fallback.
 *
 * Save = two atomic-save calls (carrier / redist) in sequence, deduplicated
 * by their natural keys before sending. Redist is workspace-level state keyed
 * only by dispatch+carrier. Missing explicit pricing means no pickup option.
 * Pickup rates are NOT saved here — they're read-only in this workspace and
 * edited on the dedicated Pickup Rates screen (/add-on-services/pickup-rates);
 * the route table still shows pickup leg costs for sanity-checking lane totals.
 */

// ============================================================================
// Shared types + helpers
// ============================================================================

interface BandColumn {
  weightFromKg: number;
  weightToKg: number;
}

interface BandCell {
  baseRateCents: number;
  perKgCents: number | null;
  anchorWeightKg: number | null;
  /** True when no rate is set — saved as "skip" so no DB row is written. */
  isEmpty: boolean;
}

function defaultCell(): BandCell {
  return {
    baseRateCents: 0,
    perKgCents: null,
    anchorWeightKg: null,
    isEmpty: true,
  };
}

function activatedCell(): BandCell {
  return {
    baseRateCents: 0,
    perKgCents: null,
    anchorWeightKg: null,
    isEmpty: false,
  };
}

function computeNextBaseCents(
  prevCell: BandCell,
  prevColumn: BandColumn
): number {
  if (prevCell.perKgCents == null || prevCell.perKgCents === 0) {
    return prevCell.baseRateCents;
  }
  const anchorKg = prevCell.anchorWeightKg ?? prevColumn.weightFromKg;
  const slopeKg = Math.max(0, prevColumn.weightToKg - anchorKg);
  return prevCell.baseRateCents + Math.round(slopeKg * prevCell.perKgCents);
}

function formatCellSummary(cell: BandCell): string {
  const base = formatCents(cell.baseRateCents);
  if (cell.perKgCents == null || cell.perKgCents === 0) {
    return cell.baseRateCents === 0 ? "Free" : `Flat ${base}`;
  }
  return `${base} + ${formatCents(cell.perKgCents)}/kg`;
}

// Pickup rates are read-only in this workspace (editing moved to the Pickup
// Rates screen). Route cell handlers edit only customer label rates; redist has
// dedicated workspace-level handlers below.
type RateType = "customer";

/**
 * ⚠️ THE RATES ARE TWO INDEPENDENT WRITES, AND ONE BASELINE CANNOT DESCRIBE TWO
 * OUTCOMES.
 *
 * `saveCarrierRateBands` and `saveRedistRateBands` are separate, non-transactional
 * calls. The label bands can land and redistribution then fail. With a single
 * baseline the whole section stayed "unsaved" — so Discard would throw away label
 * rates that are ALREADY STORED, and the bar would keep offering to re-save them.
 *
 * So the baseline is per-write, exactly like the writes.
 */
function serializeCarrierBands(
  routes: UnifiedRoute[],
  columns: BandColumn[],
  isSelfRated: boolean | undefined
): string {
  // ⚠️ THERE IS NO CARRIER WRITE FOR AN API-PROCURED CARRIER, so nothing about it
  // can be dirty. The label price comes from the carrier's API and the atomic save
  // is skipped entirely.
  //
  // This used to still serialize `columns`, so adding a weight BRACKET marked the
  // carrier bands dirty — for a write that never runs. The baseline could therefore
  // never advance, and the save bar stayed lit forever with nothing to save.
  //
  // The empty string is the honest answer: this write does not exist. The dirty
  // check short-circuits on it. (A bracket change still reaches the server: the
  // redistribution serializer carries `columns` too, and that write DOES run.)
  if (!isSelfRated) return "";

  return JSON.stringify({
    columns,
    routes: routes.map((r) => ({ key: r.key, customerCells: r.customerCells })),
  });
}

function serializeRedistBands(
  redistCells: BandCell[],
  columns: BandColumn[]
): string {
  // ⚠️ SERIALIZE THE PAYLOAD, NOT THE SCREEN.
  //
  // Redistribution is WORKSPACE-WIDE — one explicit scoped row per bracket.
  // Dirty must mean "the bytes we are about to send changed".
  return JSON.stringify({
    columns,
    redistCells,
  });
}

interface UnifiedRoute {
  /** Stable React key — "fromAreaId__toAreaId". */
  key: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  customerCells: BandCell[];
  pickupCells: BandCell[];
}

function routeKey(fromAreaId: string, toAreaId: string): string {
  return `${fromAreaId}__${toAreaId}`;
}

// ============================================================================
// Initial state from server data
// ============================================================================

interface BuildArgs {
  carrierBands: CarrierRateBand[];
  pickupBands: PickupRateBand[];
  redistBands: RedistributionRateBand[];
  workspaceDispatchId: string | null;
  workspaceCarrierId: string | null;
}

interface BuiltState {
  columns: BandColumn[];
  routes: UnifiedRoute[];
  redistCells: BandCell[];
}

function buildInitialState(args: BuildArgs): BuiltState {
  // Rate-band simplification: one row per scope+bracket. No validFrom
  // filtering, no `active` filtering. The returned bands ARE the
  // currently-effective set.
  const { carrierBands, pickupBands, redistBands } = args;
  const carrierSlot = carrierBands;
  const pickupSlot = pickupBands;
  const redistSlot = redistBands;

  const colMap = new Map<string, BandColumn>();
  // Prisma Decimal columns serialize as strings over JSON despite the
  // TypeScript types saying `number`. Coerce here so local state holds real
  // numbers — otherwise the save flow ships strings and the server's Zod
  // schema (which actually does runtime checking) rejects with type errors.
  const addCol = (b: { weightFromKg: number; weightToKg: number }) => {
    const wf = Number(b.weightFromKg);
    const wt = Number(b.weightToKg);
    const k = `${wf}__${wt}`;
    if (!colMap.has(k)) {
      colMap.set(k, { weightFromKg: wf, weightToKg: wt });
    }
  };
  carrierSlot.forEach(addCol);
  pickupSlot.forEach(addCol);
  redistSlot.forEach(addCol);
  const columns = Array.from(colMap.values()).sort(
    (a, b) => a.weightFromKg - b.weightFromKg
  );

  if (columns.length === 0) {
    return {
      columns: [],
      routes: [],
      redistCells: [],
    };
  }

  const findCell = <
    T extends {
      weightFromKg: number;
      weightToKg: number;
      baseRateCents: number;
      perKgCents: number | null;
      anchorWeightKg: number | null;
    },
  >(
    list: T[],
    col: BandColumn
  ): BandCell => {
    // col is now numeric (post-addCol coercion); band weights are still
    // Decimal-strings off the API. Coerce on the compare AND on the carried
    // anchorWeightKg so cells hold real numbers downstream.
    const m = list.find(
      (b) =>
        Number(b.weightFromKg) === col.weightFromKg &&
        Number(b.weightToKg) === col.weightToKg
    );
    return m
      ? {
          baseRateCents: m.baseRateCents,
          perKgCents: m.perKgCents,
          anchorWeightKg:
            m.anchorWeightKg == null ? null : Number(m.anchorWeightKg),
          isEmpty: false,
        }
      : defaultCell();
  };

  const workspaceRedistBands = redistSlot.filter(
    (b) =>
      b.dispatchCenterId === args.workspaceDispatchId &&
      b.carrierId === args.workspaceCarrierId
  );
  const workspaceRedistCells = columns.map((col) =>
    findCell(workspaceRedistBands, col)
  );

  const routeKeys = new Set<string>();
  for (const b of carrierSlot) {
    routeKeys.add(routeKey(b.fromServiceAreaId, b.toServiceAreaId));
  }

  const routes: UnifiedRoute[] = [];
  for (const key of routeKeys) {
    const sample = carrierSlot.find(
      (b) => routeKey(b.fromServiceAreaId, b.toServiceAreaId) === key
    );
    if (!sample) continue;
    const fromAreaId = sample.fromServiceAreaId;
    const toAreaId = sample.toServiceAreaId;

    const laneCarrierBands = carrierSlot.filter(
      (b) =>
        b.fromServiceAreaId === fromAreaId && b.toServiceAreaId === toAreaId
    );
    const customerCells = columns.map((col) => findCell(laneCarrierBands, col));

    const lanePickupBands = pickupSlot.filter(
      (b) =>
        b.fromServiceAreaId === fromAreaId &&
        b.dispatchCenterId === args.workspaceDispatchId
    );
    const pickupCells = columns.map((col) => findCell(lanePickupBands, col));

    routes.push({
      key,
      fromServiceAreaId: fromAreaId,
      toServiceAreaId: toAreaId,
      customerCells,
      pickupCells,
    });
  }

  routes.sort((a, b) => a.key.localeCompare(b.key));

  return {
    columns,
    routes,
    redistCells: workspaceRedistCells,
  };
}

// ============================================================================
// Cell editor
// ============================================================================

/**
 * Wrapped <Input> that holds a local draft string and only commits the parsed
 * value on blur. Fixes the "can't type a decimal" bug where the previous
 * fully-controlled inputs reformatted to `.toFixed(2)` on every keystroke,
 * eating the cursor and rejecting intermediate states like "4." or "4.5".
 *
 * The draft re-syncs from the prop when the input is unfocused (so external
 * updates — e.g. the next-bracket auto-compute on blur — still reflect).
 * Skipping the sync during focus is what lets the user type freely.
 */
function CellDraftInput({
  value,
  format,
  parse,
  onCommit,
  onBlur: onBlurProp,
  ...inputProps
}: {
  value: number | null;
  format: (v: number | null) => string;
  parse: (s: string) => number | null | undefined;
  onCommit: (v: number | null) => void;
  onBlur?: () => void;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "onBlur"
>) {
  const [draft, setDraft] = useState(() => format(value));
  const [focused, setFocused] = useState(false);

  // Sync down when the external value changes and we're NOT actively editing.
  useEffect(() => {
    if (!focused) setDraft(format(value));
  }, [value, focused, format]);

  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const parsed = parse(draft);
        if (parsed === undefined) {
          // unparseable — revert to canonical
          setDraft(format(value));
        } else if (parsed !== value) {
          onCommit(parsed);
        }
        onBlurProp?.();
      }}
    />
  );
}

const formatCurrency = (cents: number | null): string =>
  cents == null ? "" : (cents / 100).toFixed(2);
const parseCurrency = (s: string): number | null | undefined => {
  if (s.trim() === "") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
};

// Bands are priced independently — no continuity enforcement, no anchor.
// The slope (`perKg`) compounds from the band's lower edge (weightFromKg)
// up to its upper edge. Bands above each other are free to step.

function BandCellEditor({
  cell,
  column,
  onChange,
  onBlur,
  onActivate,
  onClear,
}: {
  cell: BandCell;
  column: BandColumn;
  onChange: (field: keyof BandCell, value: number | null) => void;
  onBlur: (field: keyof BandCell) => void;
  onActivate: () => void;
  onClear: () => void;
}) {
  const hasSlope = cell.perKgCents != null;
  // Endpoint at the upper edge of THIS band — surfaces the implied price at
  // weightToKg so admins can sanity-check the curve within the band.
  const endpointCents = cell.isEmpty ? 0 : computeNextBaseCents(cell, column);
  if (cell.isEmpty) {
    return (
      <button
        type="button"
        onClick={onActivate}
        title="Add a rate for this bracket"
        className="flex w-full items-center justify-center gap-1 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
      >
        <Plus className="h-3 w-3" />
        Add rate
      </button>
    );
  }
  return (
    <div className="relative group">
      <Button
        variant="ghost"
        size="sm"
        className="absolute -right-1 -top-1 h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={onClear}
        title="Clear this bracket — matching options become unavailable"
      >
        <Trash2 className="h-3 w-3" />
      </Button>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">$</span>
        <CellDraftInput
          type="number"
          step="0.01"
          value={cell.baseRateCents}
          format={formatCurrency}
          parse={parseCurrency}
          onCommit={(v) => onChange("baseRateCents", v ?? 0)}
          onBlur={() => onBlur("baseRateCents")}
          title="Base per shipment at the anchor weight"
          className="num h-7 text-xs w-16 px-1.5 text-right"
        />
        <span className="text-muted-foreground">+</span>
        <CellDraftInput
          type="number"
          step="0.01"
          value={cell.perKgCents}
          format={formatCurrency}
          parse={parseCurrency}
          onCommit={(v) => onChange("perKgCents", v)}
          onBlur={() => onBlur("perKgCents")}
          placeholder="—"
          title="Per-kg slope above the band's lower edge (leave blank for flat)"
          className="num h-7 text-xs w-14 px-1.5 text-right"
        />
        <span className="text-muted-foreground">/kg</span>
      </div>
      <div className="num text-[10px] text-muted-foreground mt-0.5">
        {formatCellSummary(cell)}
        {hasSlope && (cell.perKgCents ?? 0) !== 0 && (
          <>
            {" · example "}
            <span className="font-mono">{formatCents(endpointCents)}</span>
            {" at "}
            <span className="font-mono">{column.weightToKg}kg</span>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Route label
// ============================================================================

function RouteLabel({
  route,
  areas,
}: {
  route: UnifiedRoute;
  areas: { id: string; code: string; name: string }[];
}) {
  const from = areas.find((a) => a.id === route.fromServiceAreaId);
  const to = areas.find((a) => a.id === route.toServiceAreaId);
  return (
    <span className="text-sm font-medium">
      {from?.name ?? route.fromServiceAreaId}{" "}
      <span className="text-muted-foreground">→</span>{" "}
      {to?.name ?? route.toServiceAreaId}
    </span>
  );
}

// ============================================================================
// Shared-rate top section: Redistribution Rates
// ============================================================================
//
// Redistribution rate bands are keyed on (dispatch, carrier) — no area at
// all, so they're SHARED across multiple routes. Editing them inside the
// per-route table caused confusion: identical numbers appeared on every
// route, all editable, all silently synced. This top-level section lifts
// the editing out of the route table into one workspace-wide redist card.
//
// Pickup rate bands are keyed on (sender area, dispatch center). They are
// NOT edited here anymore — pickup-rate editing lives on the dedicated
// Pickup Rates screen (/add-on-services/pickup-rates). The route table
// below still SHOWS pickup + redist values for the lane, but read-only
// (so admin can sanity-check the per-lane total).

interface RedistCellHandlers {
  onCellChange: (
    colIdx: number,
    field: keyof BandCell,
    value: number | null
  ) => void;
  onCellBlur: (colIdx: number, field: keyof BandCell) => void;
  onCellActivate: (colIdx: number) => void;
  onCellClear: (colIdx: number) => void;
}

function RedistRatesSection({
  columns,
  cells,
  dispatchCode,
  carrierName,
  handlers,
}: {
  columns: BandColumn[];
  cells: BandCell[];
  dispatchCode: string;
  carrierName: string;
  handlers: RedistCellHandlers;
}) {
  if (columns.length === 0) return null;
  return (
    <Card>
      <div className="border-b px-4 py-2.5">
        <div className="text-sm font-semibold">Redistribution</div>
        <p className="text-xs text-muted-foreground">
          Dispatch center → carrier. Same for every lane —{" "}
          <span className="font-mono">{dispatchCode}</span> → {carrierName}.
        </p>
      </div>
      <CardContent className="px-3 pb-3 pt-2">
        <SharedRateSubcard
          title={null}
          columns={columns}
          cells={cells}
          onCellChange={handlers.onCellChange}
          onCellBlur={handlers.onCellBlur}
          onCellActivate={handlers.onCellActivate}
          onCellClear={handlers.onCellClear}
        />
        <div className="mt-2 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          A missing redistribution rate leaves the pickup option unavailable.
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One bracket × cell table (single rate column). Used inside the
 * Redistribution Rates top section. Cells are edited via the existing
 * BandCellEditor so behavior matches the per-route table.
 */
function SharedRateSubcard({
  title,
  columns,
  cells,
  onCellChange,
  onCellBlur,
  onCellActivate,
  onCellClear,
}: {
  title: string | null;
  columns: BandColumn[];
  cells: BandCell[];
  onCellChange: (
    colIdx: number,
    field: keyof BandCell,
    value: number | null
  ) => void;
  onCellBlur: (colIdx: number, field: keyof BandCell) => void;
  onCellActivate: (colIdx: number) => void;
  onCellClear: (colIdx: number) => void;
}) {
  return (
    <div className="rounded-md border">
      {title && (
        <div className="border-b px-4 py-2.5 text-sm font-medium">{title}</div>
      )}
      <Table className="[&_td]:px-3 [&_th]:px-3">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[140px] py-2">
              Package billable kg
            </TableHead>
            <TableHead className="py-2">Rate</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {columns.map((col, colIdx) => (
            // Stable per-bracket key (not the index): inserting/removing a
            // bracket must not make React reuse a CellDraftInput's local draft
            // for a different bracket.
            <TableRow
              key={`${col.weightFromKg}:${col.weightToKg}`}
              className="hover:bg-transparent"
            >
              <TableCell className="font-medium text-sm py-2 align-middle">
                {col.weightFromKg} – {col.weightToKg} kg
              </TableCell>
              <TableCell className="py-2 align-middle">
                <BandCellEditor
                  cell={cells[colIdx]}
                  column={col}
                  onChange={(field, value) =>
                    onCellChange(colIdx, field, value)
                  }
                  onBlur={(field) => onCellBlur(colIdx, field)}
                  onActivate={() => onCellActivate(colIdx)}
                  onClear={() => onCellClear(colIdx)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================================================
// Top-level workspace
// ============================================================================

/**
 * Persisted scope — remembers the last carrier service / pickup service /
 * dispatch / validFrom across reloads. localStorage is the right scope for
 * "I was working on Canada Post yesterday, take me back there." The stored
 * IDs are validated against the loaded lists later and cleared if stale.
 */
const SCOPE_STORAGE_KEY = "pricing-workspace-scope-v1";

interface PersistedScope {
  pickupProviderCarrierId: string | null;
  carrierServiceId: string | null;
  pickupServiceId: string | null;
  dispatchCenterId: string | null;
}

function loadPersistedScope(): Partial<PersistedScope> {
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<PersistedScope>;
  } catch {
    return {};
  }
}

function savePersistedScope(scope: PersistedScope): void {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // ignore quota / private mode
  }
}

export function PricingWorkspaceScreen() {
  // Lazy initializers read from localStorage. If the saved IDs are stale
  // (carrier service no longer active etc.), the validation effect below
  // clears them once the relevant lists load.
  const [carrierServiceId, setCarrierServiceId] = useState<string | null>(
    () => loadPersistedScope().carrierServiceId ?? null
  );
  const [selectedPickupServiceId, setPickupServiceId] = useState<string | null>(
    () => loadPersistedScope().pickupServiceId ?? null
  );
  const [selectedDispatchCenterId, setDispatchCenterId] = useState<
    string | null
  >(() => loadPersistedScope().dispatchCenterId ?? null);

  const [selectedPickupProviderId, setPickupProviderId] = useState<
    string | null
  >(() => loadPersistedScope().pickupProviderCarrierId ?? null);

  useEffect(() => {
    savePersistedScope({
      carrierServiceId,
      pickupProviderCarrierId: selectedPickupProviderId,
      pickupServiceId: selectedPickupServiceId,
      dispatchCenterId: selectedDispatchCenterId,
    });
  }, [
    carrierServiceId,
    selectedPickupProviderId,
    selectedPickupServiceId,
    selectedDispatchCenterId,
  ]);

  // Carrier picker narrows the carrier-service dropdown. This endpoint only
  // accepts a single `carrierId` — when the top-bar scope holds multiple
  // carriers we default to the first; the in-page picker drives the actual
  // selection.
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  const { data: carrierServices } = useGetCarrierServices({
    active: true,
    carrierId: scopedCarrierIds[0],
  });
  const { data: serviceAreas } = useGetServiceAreas();
  const { data: dispatchCenters, isError: dispatchCentersError } =
    useGetDispatchCenters();

  const carrierServiceList: CarrierServiceListItem[] = useMemo(
    () => carrierServices ?? [],
    [carrierServices]
  );

  const carrierService = useMemo(
    () => carrierServiceList.find((cs) => cs.id === carrierServiceId),
    [carrierServiceList, carrierServiceId]
  );
  const workspaceCarrierId = carrierService?.carrier?.id ?? null;

  // `selfRated` decides whether EZX's own rate bands are even meaningful for this
  // service: a self-rated carrier is priced FROM those bands, an externally
  // procured one is priced by the provider's API and the bands are inert.
  //
  // The server answers it. Do not re-derive it here — a client that guesses wrong
  // silently skips (or silently sends) the rate-band save.
  // `undefined` = services not loaded yet, which is NOT "externally procured".
  const isSelfRatedCarrier: boolean | undefined = carrierService?.carrier
    ? carrierService.carrier.selfRated
    : undefined;

  const { data: carriers, isError: carriersError } = useGetCarriers();
  const { data: pickupServices, isError: pickupServicesError } =
    useListCarrierAddOnServices({ type: "PICKUP" });
  const grants = useListCarrierAddOnServiceGrants(
    workspaceCarrierId
      ? { labelCarrierId: workspaceCarrierId, serviceType: "PICKUP" }
      : undefined
  );
  const access = useListCarrierAddOnServiceAccess(
    workspaceCarrierId ? { labelCarrierId: workspaceCarrierId } : undefined
  );
  const pickupSetupError =
    pickupServicesError || grants.isError || access.isError || carriersError;
  const pickupOptionsReady =
    !!pickupServices &&
    !!grants.data &&
    !!access.data &&
    !!carriers &&
    !pickupSetupError;
  const eligiblePickupServices = useMemo(() => {
    if (!workspaceCarrierId || pickupSetupError) return [];
    return (pickupServices ?? []).filter(
      (service) =>
        service.active &&
        service.type === "PICKUP" &&
        grants.data?.some(
          (grant) =>
            grant.providerCarrierId === service.providerCarrierId &&
            grant.labelCarrierId === workspaceCarrierId &&
            grant.serviceType === "PICKUP"
        ) &&
        access.data?.some(
          (row) =>
            row.carrierAddOnServiceId === service.id &&
            row.labelCarrierId === workspaceCarrierId &&
            row.enabled
        )
    );
  }, [
    workspaceCarrierId,
    pickupSetupError,
    pickupServices,
    grants.data,
    access.data,
  ]);
  const pickupOperators = useMemo(
    () =>
      (carriers?.data ?? []).filter(
        (carrier) =>
          carrier.active &&
          eligiblePickupServices.some(
            (service) => service.providerCarrierId === carrier.id
          )
      ),
    [carriers, eligiblePickupServices]
  );
  const pickupProviderCarrierId = pickupOperators.some(
    (operator) => operator.id === selectedPickupProviderId
  )
    ? selectedPickupProviderId
    : null;
  useEffect(() => {
    if (!pickupOptionsReady) return;
    if (
      !pickupOperators.some(
        (operator) => operator.id === selectedPickupProviderId
      )
    ) {
      setPickupProviderId(
        pickupOperators.find((operator) => operator.id === workspaceCarrierId)
          ?.id ??
          pickupOperators[0]?.id ??
          null
      );
    }
  }, [
    pickupOperators,
    selectedPickupProviderId,
    workspaceCarrierId,
    pickupOptionsReady,
  ]);
  const pickupServiceList = useMemo(
    () =>
      eligiblePickupServices.filter(
        (service) => service.providerCarrierId === pickupProviderCarrierId
      ),
    [eligiblePickupServices, pickupProviderCarrierId]
  );
  const pickupServiceId = pickupServiceList.some(
    (service) => service.id === selectedPickupServiceId
  )
    ? selectedPickupServiceId
    : null;
  const dispatchList = useMemo(
    () =>
      (dispatchCentersError ? [] : (dispatchCenters ?? []))
        .filter(
          (center) =>
            !!pickupProviderCarrierId &&
            center.pickupProviderCarrierId === pickupProviderCarrierId &&
            center.isActive
        )
        .map((center) => ({
          id: center.id,
          code: center.code,
          name: center.name,
        })),
    [dispatchCenters, pickupProviderCarrierId, dispatchCentersError]
  );
  const dispatchCenterId = dispatchList.some(
    (center) => center.id === selectedDispatchCenterId
  )
    ? selectedDispatchCenterId
    : null;

  const areasList = useMemo(
    () =>
      (serviceAreas ?? []).map((a) => ({
        id: a.id,
        code: a.code,
        name: a.name,
      })),
    [serviceAreas]
  );

  // Validate persisted IDs against loaded lists — clear if stale (carrier
  // service deleted, dispatch retired, etc.) so the auto-pick effects below
  // can fill in a sane default instead of leaving the workspace broken.
  useEffect(() => {
    if (
      carrierServiceId &&
      carrierServiceList.length > 0 &&
      !carrierServiceList.some((cs) => cs.id === carrierServiceId)
    ) {
      setCarrierServiceId(null);
    }
  }, [carrierServiceList, carrierServiceId]);

  // Auto-pick a default within the (carrier-scoped) list. Fires when nothing
  // is selected, when the persisted/selected id is no longer in the list
  // (carrier changed, service retired), and as a single-option collapse
  // (length === 1 + different id → snap to the only option).
  useEffect(() => {
    if (
      pickupServiceList.length >= 1 &&
      (pickupServiceId === null ||
        !pickupServiceList.some((p) => p.id === pickupServiceId))
    ) {
      const firstActive =
        pickupServiceList.find((p) => p.active) ?? pickupServiceList[0];
      setPickupServiceId(firstActive.id);
    }
  }, [pickupServiceList, pickupServiceId]);

  useEffect(() => {
    if (
      dispatchList.length >= 1 &&
      (dispatchCenterId === null ||
        !dispatchList.some((d) => d.id === dispatchCenterId))
    ) {
      setDispatchCenterId(dispatchList[0].id);
    }
  }, [dispatchList, dispatchCenterId]);

  const carrierFilter = carrierServiceId ? { carrierServiceId } : undefined;
  const {
    data: carrierBands,
    isSuccess: carrierBandsReady,
    isError: carrierBandsError,
    refetch: refetchCarrier,
  } = useGetCarrierRateBands(carrierFilter);
  // Pickup bands are read-only in this workspace — fetched purely so the
  // route table can SHOW the pickup leg cost for each lane. Editing happens
  // on the dedicated Pickup Rates screen.
  // Scope to the current workspace dispatch — PickupRateBand rows are keyed
  // on (carrierAddOnServiceId, fromServiceAreaId, dispatchCenterId, ...) and
  // the workspace post-filters by dispatchCenterId when assembling cells.
  // Without this filter, the fetch returns rows for all dispatch centers
  // and the local filter can silently drop cells when contexts mismatch.
  const pickupFilter =
    pickupServiceId && dispatchCenterId
      ? {
          carrierAddOnServiceId: pickupServiceId,
          dispatchCenterId,
        }
      : undefined;
  const {
    data: pickupBands,
    isSuccess: pickupBandsReady,
    isError: pickupBandsError,
  } = useGetPickupRateBands(pickupFilter);
  const redistFilter = workspaceCarrierId
    ? { carrierId: workspaceCarrierId }
    : undefined;
  const {
    data: redistBands,
    isSuccess: redistBandsReady,
    isError: redistBandsError,
    refetch: refetchRedist,
  } = useGetRedistributionRateBands(redistFilter);
  const initial = useMemo<BuiltState>(() => {
    if (!carrierBands || !pickupBands || !redistBands) {
      return {
        columns: [],
        routes: [],
        redistCells: [],
      };
    }
    return buildInitialState({
      carrierBands,
      pickupBands,
      redistBands,
      workspaceDispatchId: dispatchCenterId,
      workspaceCarrierId,
    });
  }, [
    carrierBands,
    pickupBands,
    redistBands,
    dispatchCenterId,
    workspaceCarrierId,
  ]);

  const [columns, setColumns] = useState<BandColumn[]>(initial.columns);
  const [routes, setRoutes] = useState<UnifiedRoute[]>(initial.routes);
  const [redistCells, setRedistCells] = useState<BandCell[]>(
    initial.redistCells
  );

  // ⚠️ THIS EFFECT MUST NEVER CLOBBER STAGED WORK.
  //
  // It reseeds the tables from the server. It used to fire on ANY change to
  // `initial` — so a refetch landing while the user was typing (the save's own
  // refetch is fire-and-forget, and its response can arrive after the button
  // re-enables) would overwrite the newer edits with server state.
  //
  // But it MUST still reseed when the SCOPE changes: those staged edits belong to
  // the carrier service you just navigated away from, and carrying them onto a
  // different service is how you write one carrier's rates onto another.
  //
  // So: always reseed on a scope change; on a same-scope refresh, never overwrite
  // an edit the user has not saved.
  // `isSelfRatedCarrier` is part of the key, not just a flag: it changes the SHAPE
  // of the serialization (a self-rated carrier's label cells are writable; an
  // API-procured one's are not). It also arrives late — `undefined` until the
  // carriers load. Without it here, the flag flipping `undefined → true` would make
  // the current tables mismatch a snapshot taken under the old shape, which reads
  // as a user edit, skips the reseed, and leaves the rates falsely dirty.
  const scopeKey = `${carrierServiceId ?? ""}|${pickupServiceId ?? ""}|${dispatchCenterId ?? ""}|${String(isSelfRatedCarrier)}`;
  const lastScopeKey = useRef(scopeKey);

  /**
   * ⚠️ "DIRTY" MEANS "DIFFERENT FROM WHAT WE PUT ON SCREEN", NOT "DIFFERENT FROM
   * THE SERVER". Those are not the same thing, and conflating them is a bug.
   *
   * My first attempt guarded the reseed with `ratesDirty` — routes vs the CURRENT
   * `initial`. But when the server changes and the user has touched nothing, that
   * comparison is ALSO different (old local vs new server). The guard read a
   * server refresh as a local edit, skipped the reseed, and left stale numbers on
   * screen that a later save would write back over the fresher ones.
   *
   * The honest baseline is the snapshot we last SEEDED the tables from. Compare
   * against that and "dirty" means exactly one thing: the user typed something.
   */
  // STATE, not refs: the dirty flags derive from these, and a ref mutation does not
  // re-render — the save bar would stay lit after a successful save.
  //
  // TWO seeds, because there are TWO writes. See serializeCarrierBands.
  const [seededCarrier, setSeededCarrier] = useState<string>("");
  const [seededRedist, setSeededRedist] = useState<string>("");
  const seeded = seededCarrier !== "" || seededRedist !== "";

  useEffect(() => {
    const scopeChanged = lastScopeKey.current !== scopeKey;
    const nextCarrier = serializeCarrierBands(
      initial.routes,
      initial.columns,
      isSelfRatedCarrier
    );
    const nextRedist = serializeRedistBands(
      initial.redistCells,
      initial.columns
    );
    const userEdited =
      seeded &&
      (serializeCarrierBands(routes, columns, isSelfRatedCarrier) !==
        seededCarrier ||
        serializeRedistBands(redistCells, columns) !== seededRedist);

    // Always reseed on a scope change: those edits belong to the carrier service
    // you navigated away from, and carrying them across is how one carrier's rates
    // get written onto another. Otherwise, never overwrite work the user has not
    // saved.
    if (!scopeChanged && userEdited) {
      lastScopeKey.current = scopeKey;
      return;
    }

    lastScopeKey.current = scopeKey;
    setSeededCarrier(nextCarrier);
    setSeededRedist(nextRedist);
    setColumns(initial.columns);
    setRoutes(initial.routes);
    setRedistCells(initial.redistCells);
    // `routes`/`columns`/`redistCells` are read to detect a user edit, but must NOT retrigger
    // this effect — it reseeds them, which would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, scopeKey, isSelfRatedCarrier]);

  // ── What is staged but not stored ─────────────────────────────────────────
  // `initial` is derived straight from the server, so it IS the baseline. Before
  // this, nothing tracked dirtiness at all — "Save all rates" simply rewrote
  // every band on every click, and the screen could never tell you whether the
  // number in front of you was saved.
  // ⚠️ Compare only what SAVE ACTUALLY WRITES.
  //
  // A whole-object compare also diffs `pickupCells`, which are READ-ONLY here —
  // pickup rates are edited on their own screen. A pickup refetch that changed
  // nothing the user can touch would mark the rates dirty and offer to save them.
  // "Dirty" has to mean "you changed something that will be written", or the bar
  // cries wolf and people stop reading it.
  const carrierBandsDirty = useMemo(
    () =>
      seededCarrier !== "" &&
      serializeCarrierBands(routes, columns, isSelfRatedCarrier) !==
        seededCarrier,
    [routes, columns, isSelfRatedCarrier, seededCarrier]
  );
  const redistBandsDirty = useMemo(
    () =>
      seededRedist !== "" &&
      serializeRedistBands(redistCells, columns) !== seededRedist,
    [redistCells, columns, seededRedist]
  );
  const ratesDirty = carrierBandsDirty || redistBandsDirty;

  // The take rate lives in its own component; it reports up rather than saving
  // itself. See the note on MarkupConfig — two save models on one money screen
  // is the bug this replaces.
  const markupRef = useRef<MarkupConfigHandle>(null);
  const [markupDirty, setMarkupDirty] = useState(false);
  const [markupSaving, setMarkupSaving] = useState(false);
  const [markupLoadError, setMarkupLoadError] = useState(false);
  const handleMarkupLoadError = useCallback(
    (v: boolean) => setMarkupLoadError(v),
    []
  );
  const handleMarkupDirty = useCallback((d: boolean) => setMarkupDirty(d), []);
  const handleMarkupSaving = useCallback(
    (v: boolean) => setMarkupSaving(v),
    []
  );

  // Dim factor (editable) ---------------------------------------------------
  const [dimFactorDraft, setDimFactorDraft] = useState<string>("");
  // Same seeded-baseline rule as the tables and the take rate.
  //  - Keyed on the SERVICE, not just the value: two services storing the same dim
  //    factor (or both storing none) would otherwise never re-fire this, leaving
  //    the previous service's draft on screen for a save to write onto the new one.
  //  - Never clobber an unsaved edit on a data refresh: a save invalidates this
  //    query, so anything typed between the click and the response would be lost.
  const [dimFactorSeeded, setDimFactorSeeded] = useState<string>("");
  const lastDimServiceId = useRef(carrierServiceId);

  useEffect(() => {
    const stored =
      carrierService?.dimFactorCm != null
        ? String(Number(carrierService.dimFactorCm))
        : "";
    const scopeChanged = lastDimServiceId.current !== carrierServiceId;
    lastDimServiceId.current = carrierServiceId;
    const edited = dimFactorDraft.trim() !== dimFactorSeeded;
    if (!scopeChanged && edited) return;
    setDimFactorSeeded(stored);
    setDimFactorDraft(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draft/seeded are read
    // to detect an edit; taking them as deps would reseed on every keystroke.
  }, [carrierService?.dimFactorCm, carrierServiceId]);

  // No toast here — the one save bar reports the whole save once.
  const updateCarrierServiceMut = useUpdateCarrierService({});

  // Against what we SEEDED, not against live server data — otherwise a refresh
  // reads as a user edit.
  const dimFactorDirty = dimFactorDraft.trim() !== dimFactorSeeded;

  /**
   * Returns what was ACTUALLY STORED, canonicalised.
   *
   * ⚠️ The draft is not the value. `0`, `-1` and gibberish all write `null` (no dim
   * factor). Re-baselining to the raw draft therefore left the screen showing "0",
   * calling itself clean, while the server held `null` — the UI and the database
   * quietly disagreeing about a number that scales every dimensional-weight price.
   */
  async function saveDimFactor(): Promise<string> {
    if (!carrierServiceId) return dimFactorSeeded;
    const num = parseFloat(dimFactorDraft);
    const stored = Number.isFinite(num) && num > 0 ? num : null;
    await updateCarrierServiceMut.mutateAsync({
      id: carrierServiceId,
      input: { dimFactorCm: stored },
    });
    return stored === null ? "" : String(stored);
  }

  // Modal state -------------------------------------------------------------
  const [addBracketOpen, setAddBracketOpen] = useState(false);
  const [addBracketFrom, setAddBracketFrom] = useState("");
  const [addBracketTo, setAddBracketTo] = useState("");

  const [addRouteOpen, setAddRouteOpen] = useState(false);
  const [addRouteFromAreaId, setAddRouteFromAreaId] = useState("");
  const [addRouteToAreaId, setAddRouteToAreaId] = useState("");

  // Copy route dialog state. When `copySourceIdx` is set, the dialog is open
  // and clones the source route's cells onto a new (from, to) the admin picks.
  const [copySourceIdx, setCopySourceIdx] = useState<number | null>(null);
  const [copyFromAreaId, setCopyFromAreaId] = useState("");
  const [copyToAreaId, setCopyToAreaId] = useState("");

  // Cell edit handlers ------------------------------------------------------
  function editCell(
    routeIdx: number,
    _rateType: RateType,
    colIdx: number,
    field: keyof BandCell,
    value: number | null
  ): void {
    setRoutes((prev) => {
      if (!prev[routeIdx]) return prev;
      return prev.map((route, i) => {
        if (i !== routeIdx) return route;
        const customerCells = route.customerCells.map((c, j) =>
          j === colIdx ? { ...c, [field]: value, isEmpty: false } : c
        );
        return { ...route, customerCells };
      });
    });
  }

  /** Mark a cell empty — on save, no DB row is written for that bracket. */
  function clearCell(
    routeIdx: number,
    _rateType: RateType,
    colIdx: number
  ): void {
    setRoutes((prev) => {
      if (!prev[routeIdx]) return prev;
      return prev.map((route, i) => {
        if (i !== routeIdx) return route;
        const customerCells = route.customerCells.map((c, j) =>
          j === colIdx ? defaultCell() : c
        );
        return { ...route, customerCells };
      });
    });
  }

  /** Activate an empty cell so the user can start filling values in. */
  function activateCell(
    routeIdx: number,
    _rateType: RateType,
    colIdx: number
  ): void {
    setRoutes((prev) => {
      if (!prev[routeIdx]) return prev;
      return prev.map((route, i) => {
        if (i !== routeIdx) return route;
        const customerCells = route.customerCells.map((c, j) =>
          j === colIdx ? activatedCell() : c
        );
        return { ...route, customerCells };
      });
    });
  }

  // Bands are priced independently (no continuity, no anchor) — blur no
  // longer needs to chain a next-band auto-compute. Keeping the hook
  // signature so cell editors can still trigger refreshes if needed.
  function handleCellBlur(
    _routeIdx: number,
    _rateType: RateType,
    _colIdx: number,
    _field: keyof BandCell
  ): void {
    // intentionally empty
  }

  function editRedistCell(
    colIdx: number,
    field: keyof BandCell,
    value: number | null
  ): void {
    setRedistCells((prev) =>
      prev.map((cell, i) =>
        i === colIdx ? { ...cell, [field]: value, isEmpty: false } : cell
      )
    );
  }

  function clearRedistCell(colIdx: number): void {
    setRedistCells((prev) =>
      prev.map((cell, i) => (i === colIdx ? defaultCell() : cell))
    );
  }

  function activateRedistCell(colIdx: number): void {
    setRedistCells((prev) =>
      prev.map((cell, i) => (i === colIdx ? activatedCell() : cell))
    );
  }

  function handleRedistCellBlur(_colIdx: number, _field: keyof BandCell): void {
    // Bands are independent; no continuity work is needed on blur.
  }

  // Add / remove bracket ----------------------------------------------------
  function handleAddBracket(): void {
    const from = parseFloat(addBracketFrom);
    const to = parseFloat(addBracketTo);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
      toast.error("Invalid bracket", {
        description: "Upper weight must be greater than lower weight.",
      });
      return;
    }
    // Reject a duplicate range: two columns with the same (from, to) would share
    // a React key across the matrix/redist/bracket-bar and let React reuse the
    // wrong cell's draft state. A bracket range is the column's identity.
    if (columns.some((c) => c.weightFromKg === from && c.weightToKg === to)) {
      toast.error("Bracket already exists", {
        description: `A ${from} – ${to} kg bracket is already in this scope.`,
      });
      return;
    }
    const newCol: BandColumn = { weightFromKg: from, weightToKg: to };
    const newCols = [...columns, newCol].sort(
      (a, b) => a.weightFromKg - b.weightFromKg
    );
    const insertIdx = newCols.indexOf(newCol);
    setColumns(newCols);
    setRedistCells((prev) => {
      const next = [...prev];
      next.splice(insertIdx, 0, defaultCell());
      return next;
    });
    setRoutes((prev) => {
      return prev.map((route) => {
        const insertCells = (cells: BandCell[]) => {
          const next = [...cells];
          next.splice(insertIdx, 0, defaultCell());
          return next;
        };
        return {
          ...route,
          customerCells: insertCells(route.customerCells),
          pickupCells: insertCells(route.pickupCells),
        };
      });
    });
    setAddBracketOpen(false);
    setAddBracketFrom("");
    setAddBracketTo("");
  }

  function handleRemoveBracket(colIdx: number): void {
    setColumns((cols) => cols.filter((_, i) => i !== colIdx));
    setRedistCells((cells) => cells.filter((_, i) => i !== colIdx));
    setRoutes((prev) =>
      prev.map((route) => ({
        ...route,
        customerCells: route.customerCells.filter((_, i) => i !== colIdx),
        pickupCells: route.pickupCells.filter((_, i) => i !== colIdx),
      }))
    );
  }

  // Add / remove explicit label lane ----------------------------------------
  function handleAddRoute(): void {
    if (!addRouteFromAreaId || !addRouteToAreaId) {
      toast.error("Pick areas", {
        description: "Both origin and destination service areas are required.",
      });
      return;
    }
    insertRoute(addRouteFromAreaId, addRouteToAreaId);
    setAddRouteOpen(false);
    setAddRouteFromAreaId("");
    setAddRouteToAreaId("");
  }

  function insertRoute(fromAreaId: string, toAreaId: string): void {
    const key = routeKey(fromAreaId, toAreaId);
    if (routes.some((r) => r.key === key)) {
      toast.error("Route already exists", {
        description: "A row for this origin → destination is already present.",
      });
      return;
    }
    // Pickup is shared by origin, so inherit from a sibling in the same group.
    // Customer rates start empty because each lane owns its label price.
    const samplePickupRoute = routes.find(
      (r) => r.fromServiceAreaId === fromAreaId
    );
    const newRoute: UnifiedRoute = {
      key,
      fromServiceAreaId: fromAreaId,
      toServiceAreaId: toAreaId,
      customerCells: columns.map(() => defaultCell()),
      pickupCells: samplePickupRoute
        ? samplePickupRoute.pickupCells.map((c) => ({ ...c }))
        : columns.map(() => defaultCell()),
    };
    setRoutes((prev) => {
      const next = [...prev, newRoute];
      next.sort((a, b) => a.key.localeCompare(b.key));
      return next;
    });
  }

  // Copy route — clones the source route's cell arrays onto a new
  // (fromArea, toArea) pair. Useful when several lanes share the exact same
  // pricing structure but the admin doesn't want to retype it.
  //
  // The new route's cells are deep copies so subsequent edits don't bleed
  // across sibling routes.
  function handleCopyRoute(): void {
    if (copySourceIdx === null) return;
    if (!copyFromAreaId || !copyToAreaId) {
      toast.error("Pick areas", {
        description: "Both origin and destination service areas are required.",
      });
      return;
    }
    if (copyFromAreaId === copyToAreaId) {
      // Intra-area is allowed by the schema but admin almost always
      // mis-clicks — gently warn instead of hard-block.
    }
    const source = routes[copySourceIdx];
    if (!source) return;
    const key = routeKey(copyFromAreaId, copyToAreaId);
    if (routes.some((r) => r.key === key)) {
      toast.error("Route already exists", {
        description: "A row for this origin → destination is already present.",
      });
      return;
    }
    const cloned: UnifiedRoute = {
      key,
      fromServiceAreaId: copyFromAreaId,
      toServiceAreaId: copyToAreaId,
      customerCells: source.customerCells.map((c) => ({ ...c })),
      pickupCells: source.pickupCells.map((c) => ({ ...c })),
    };
    setRoutes((prev) => {
      const next = [...prev, cloned];
      next.sort((a, b) => a.key.localeCompare(b.key));
      return next;
    });
    toast.success("Lane copied", {
      description: "Label rates duplicated. Save all changes to persist.",
    });
    setCopySourceIdx(null);
    setCopyFromAreaId("");
    setCopyToAreaId("");
  }

  function handleDeleteRoute(routeIdx: number): void {
    setRoutes((prev) => prev.filter((_, i) => i !== routeIdx));
  }

  // Save all (carrier → redist) ---------------------------------------------
  // Pickup rates are NOT saved from this workspace — they're read-only here
  // and edited on the dedicated Pickup Rates screen.
  const saveCarrierMut = useAtomicSaveCarrierRateBands();
  const saveRedistMut = useAtomicSaveRedistRateBands();

  /**
   * ⚠️ ONE FLAG FOR THE WHOLE CYCLE, not a union of the mutations' pending states.
   *
   * The writes run in SEQUENCE (rates → take rate → dim factor). Between two of
   * them, every `isPending` is false — so a union flickers back to "not saving"
   * mid-cycle, the lock lifts for a frame, and the user can type or switch scope
   * into the middle of a save. Worse, the take rate's pending state reaches this
   * component through an effect, so it arrives a render LATE.
   *
   * `saveCycleActive` is true from the first line of handleSaveAll to the last.
   * The mutation flags stay in the union anyway: they also cover a write fired
   * from somewhere other than the bar.
   */
  /**
   * ⚠️ NOTHING IS EDITABLE UNTIL THE SERVER DATA HAS LANDED.
   *
   * On a reload with a persisted scope, the tables render before the rate bands
   * arrive. Type into them in that window and the "protect unsaved edits" guard
   * does its job — against you: it decides you have edited, so when the REAL stored
   * rates finally arrive it refuses to reseed, and a save then writes your edit on
   * top of an empty baseline. The rates that were in the database are gone.
   *
   * A first load is not a refresh. Lock until it has happened.
   */
  /**
   * ⚠️ UNLOCK ONLY WHEN WE KNOW WHAT THE SERVER HAS. `isSuccess`, not `!isLoading`.
   *
   * A FAILED query sets `isLoading` to false while `data` stays `undefined`. Keying
   * the lock on loading therefore unlocked the screen on a failure, seeded the
   * tables EMPTY, and let a save write that emptiness over real, existing rates.
   * "The request finished" and "we know the rates" are not the same sentence.
   *
   * All three queries, because `initial` is built from all three. Missing one is
   * the same bug in a different seat.
   */
  const ratesReady = carrierBandsReady && redistBandsReady && pickupBandsReady;
  // Every query whose failure LOCKS the screen must also be able to EXPLAIN it.
  // A permanent lock with no reason is its own bug.
  const loadError =
    carrierBandsError ||
    redistBandsError ||
    pickupBandsError ||
    markupLoadError;
  const hydrating = !ratesReady;

  const [saveCycleActive, setSaveCycleActive] = useState(false);
  const isSaving =
    saveCycleActive ||
    hydrating ||
    saveCarrierMut.isPending ||
    saveRedistMut.isPending ||
    markupSaving ||
    updateCarrierServiceMut.isPending;

  async function handleSaveAll(): Promise<void> {
    // ⚠️ THESE GUARDS ONLY GATE THE RATES.
    //
    // They used to abort the WHOLE save. But the take rate and dim factor are
    // editable with nothing but a carrier service picked — so a markup-only edit
    // hit a "Pick scope" error and could not be saved at all. A guard that blocks
    // a section it does not govern is just a way to lose an edit.
    // ⚠️ AN EMPTY TABLE IS A WRITE, NOT A REASON TO REFUSE ONE.
    //
    // This also demanded `routes.length > 0 && columns.length > 0`. So deleting the
    // last route or the last bracket could never be SAVED: the screen showed the
    // table empty, the save silently refused, and the old rates stayed live in the
    // database — still pricing shipments, invisibly.
    //
    // The atomic saves take `bands: []`. Emptying the table IS the deletion.
    const ratesSavable =
      !!carrierServiceId && !!pickupServiceId && !!dispatchCenterId;

    // ⚠️ SKIP, do not RETURN. This used to abort the whole save, so a markup-only
    // edit made while the rate scope was incomplete could not be saved at all —
    // the guard killed the sections it does not govern.
    const skipRates = ratesDirty && !ratesSavable;
    // ⚠️ Say nothing YET. Announcing "everything else was saved" before the save
    // has run produced contradictory pairs — a "Rates not saved" warning followed
    // by "Nothing to save", or by a failure. One message, at the end, that is true.
    const missingScope = !carrierServiceId
      ? "a carrier service"
      : !pickupServiceId
        ? "a pickup service"
        : !dispatchCenterId
          ? "a dispatch center"
          : "at least one weight bracket and one route";

    const carrierFlat: AtomicSaveBandInput[] = [];
    // Empty cells are skipped — no DB row is written, so that lane/bracket is
    // deliberately unavailable to quoting.
    for (const route of routes) {
      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        const cell = route.customerCells[i];
        if (cell.isEmpty) continue;
        carrierFlat.push({
          fromServiceAreaId: route.fromServiceAreaId,
          toServiceAreaId: route.toServiceAreaId,
          weightFromKg: col.weightFromKg,
          weightToKg: col.weightToKg,
          baseRateCents: cell.baseRateCents,
          perKgCents: cell.perKgCents,
          anchorWeightKg: cell.anchorWeightKg,
        });
      }
    }

    // Pickup rate bands are NOT saved here — they're read-only in this
    // workspace and edited on the dedicated Pickup Rates screen.

    const redistFlat: AtomicSaveRedistBandInput[] = [];
    if (dispatchCenterId && workspaceCarrierId) {
      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        const cell = redistCells[i];
        if (cell.isEmpty) continue;
        redistFlat.push({
          dispatchCenterId,
          carrierId: workspaceCarrierId,
          weightFromKg: col.weightFromKg,
          weightToKg: col.weightToKg,
          baseRateCents: cell.baseRateCents,
          perKgCents: cell.perKgCents,
          anchorWeightKg: cell.anchorWeightKg,
        });
      }
    }

    // Server allows empty bands[] = "this slot has no bands" (delete-only).
    // For externally procured carriers, skip the carrier-rate-bands call
    // entirely — those rows aren't relevant; label cost comes from the
    // carrier API.
    //
    // validFrom is a write-only timestamp on save now — required by the API
    // contract but not used by the quote engine post-simplification. Stamp
    // with `now` so the column still records when the row last changed.
    const validFrom = new Date().toISOString();

    // ⚠️ ONE SAVE. Everything the screen has staged goes in this try, so the
    // button can never be a lie about what it wrote. The take rate and dim factor
    // used to write themselves on their own identical-looking Save buttons, which
    // "Save all rates" did NOT cover — so you could edit the take rate, press the
    // big Save, and it was not in it.
    const saved: string[] = [];
    // ⚠️ REFETCH ONLY WHAT ACTUALLY SAVED.
    //
    // This was a `finally { refetchCarrier(); refetchRedist(); }` and it was a
    // DATA-LOSS BUG: a refetch recomputes `initial`, and the sync effect below
    // reseeds `routes`/`columns` from it. So a FAILED save refetched, reseeded,
    // and silently overwrote the user's unsaved edits with server state — while
    // `ratesDirty` flipped to false, hiding that anything had been lost.
    //
    // A refetch is only safe for a section whose write LANDED, where the server
    // now agrees with the screen. Never refetch a section that failed.
    let ratesLanded = false;
    setSaveCycleActive(true);
    try {
      if (ratesDirty && !skipRates) {
        // `ratesSavable` was verified above, so the scope is present. The compiler
        // cannot see that through the conditional guard — make the invariant loud
        // rather than silently `!`-asserting it.
        if (!carrierServiceId) {
          throw new Error(
            "Unreachable: rates are dirty and savable, but no carrier service is scoped."
          );
        }
        // Accounted separately: the carrier bands can land and redist then fail.
        // Reporting them as one "rates" unit would tell the user nothing saved
        // when half of it did.
        // ⚠️ Each write re-baselines ITSELF, immediately. If the label bands land
        // and redistribution then throws, the label bands are STORED — and a
        // baseline that still says otherwise would let Discard throw away rates
        // that are already saved.
        if (isSelfRatedCarrier && carrierBandsDirty) {
          await saveCarrierMut.mutateAsync({
            carrierServiceId,
            validFrom,
            bands: carrierFlat,
            // The rows this screen was built from. `carrierBands` is scoped to
            // exactly the slice the Server replaces, so it is the honest basis.
            // Without it the save overwrites whatever another editor stored in
            // the meantime, and the reverted edit is a price.
            basis: (carrierBands ?? []).map((band) => ({
              id: band.id,
              updatedAt: band.updatedAt,
            })),
          });
          setSeededCarrier(
            serializeCarrierBands(routes, columns, isSelfRatedCarrier)
          );
          ratesLanded = true;
          saved.push("label rates");
        }
        if (redistBandsDirty) {
          if (!dispatchCenterId || !workspaceCarrierId) {
            throw new Error(
              "Unreachable: redistribution rates are dirty and savable, but their scope is incomplete."
            );
          }
          await saveRedistMut.mutateAsync({
            validFrom,
            bands: redistFlat,
            scopes: [
              {
                dispatchCenterId,
                carrierId: workspaceCarrierId,
              },
            ],
          });
          setSeededRedist(serializeRedistBands(redistCells, columns));
          ratesLanded = true;
          saved.push("redistribution rates");
        }
      }
      if (markupDirty) {
        // Reports each write as it lands, so a carrier-wide fee that saved before
        // the override failed is not announced as "nothing saved".
        await markupRef.current?.save((label) => saved.push(label));
      }
      if (dimFactorDirty) {
        // Re-baseline to what LANDED, and show it. Not to what was typed.
        const stored = await saveDimFactor();
        setDimFactorSeeded(stored);
        setDimFactorDraft(stored);
        saved.push("dim factor");
      }

      if (skipRates && saved.length === 0) {
        // The ONLY thing that was dirty is the one thing we cannot write.
        toast.error("Rates not saved", {
          description: `Select ${missingScope} above to save them.`,
        });
      } else if (skipRates) {
        toast.warning("Saved, except the rates", {
          description: `Updated ${saved.join(", ")}. The rates need ${missingScope} selected above.`,
        });
      } else {
        toast.success(saved.length ? "Saved" : "Nothing to save", {
          description: saved.length
            ? `Updated ${saved.join(", ")}.`
            : "Nothing on this screen has changed.",
        });
      }
    } catch (err) {
      // ⚠️ PARTIAL SUCCESS IS THE DANGEROUS CASE, and the honest one to report.
      //
      // The sections save in sequence. If the rates land and the take rate then
      // throws, the rates ARE written — telling the user a flat "Save failed"
      // would send them back to re-enter numbers that are already stored.
      //
      // So: say exactly what landed and what did not.
      // The Server's sentence, not axios's. A stale-basis refusal says which
      // rates moved and what to do; "Request failed with status code 409" tells
      // the user nothing and reads as a transient blip worth retrying — which
      // would revert the other editor again.
      const reason = failureReason(err, "Unknown error");
      toast.error(saved.length ? "Partly saved" : "Save failed", {
        description: saved.length
          ? `Saved ${saved.join(", ")}. The rest did not: ${reason}`
          : reason,
      });
    }

    // Only now, and only if the rate write actually landed. Reseeding from the
    // server is safe exactly when the server already agrees with the screen.
    if (ratesLanded) {
      // Awaited, not fire-and-forget: the lock must still be on when the reseed
      // lands, or the response can arrive after the screen is editable again and
      // overwrite whatever was typed in the gap.
      await Promise.allSettled([refetchCarrier(), refetchRedist()]);
    }
    setSaveCycleActive(false);
  }

  /** Every unsaved section on this screen, named. The bar says which. */
  const dirtySections = [
    ratesDirty && "rates",
    markupDirty && "take rate",
    dimFactorDirty && "dim factor",
  ].filter(Boolean) as string[];

  // The bar appears whenever ANY section is dirty. It used to require the full
  // rate scope (carrier service + pickup + dispatch) — but the take rate and dim
  // factor are editable with just a carrier service, so those edits could go dirty
  // with no save and no discard control. A dirty section you cannot save is an
  // edit the user cannot keep.
  const scopeReady =
    !!carrierServiceId && !!pickupServiceId && !!dispatchCenterId;

  // ── What this screen already knows, said once, at the top ─────────────────
  // Every fact below was already ON the screen — spread across a route table, a
  // fee preview and a verdict line three cards apart. A pricer had to assemble
  // "this lane is unpriced AND earns us nothing" themselves. Summary before
  // detail: if the screen knows, the screen says so.
  const unpricedLaneBrackets = routes.reduce(
    (total, route) =>
      total + route.customerCells.filter((cell) => cell.isEmpty).length,
    0
  );

  /** Throw the staged edits away — re-seed every draft from the server. */
  function handleDiscardAll(): void {
    setColumns(initial.columns);
    setRoutes(initial.routes);
    setRedistCells(initial.redistCells);
    // Re-baseline: Discard means "the screen is the server again". Without this,
    // a server refresh that arrived while edits were protected would leave the
    // tables comparing against a stale snapshot and still reading as dirty.
    setSeededCarrier(
      serializeCarrierBands(initial.routes, initial.columns, isSelfRatedCarrier)
    );
    setSeededRedist(serializeRedistBands(initial.redistCells, initial.columns));
    // From the CURRENT server value, not the stale seed — a refresh may have landed
    // while the edit was protected, and Discard means "the screen is the server
    // again", not "the screen is whatever it was last time I looked".
    const dimNow =
      carrierService?.dimFactorCm != null
        ? String(Number(carrierService.dimFactorCm))
        : "";
    setDimFactorSeeded(dimNow);
    setDimFactorDraft(dimNow);
    // The take rate keeps its own draft state — a refetch alone would not
    // reliably re-seed it, and a Discard that silently leaves one section dirty
    // is worse than no Discard at all.
    markupRef.current?.reset();
    void refetchCarrier();
    void refetchRedist();
    toast.success("Discarded", {
      description: "Every unsaved change on this screen was thrown away.",
    });
  }

  const dispatchCode =
    dispatchList.find((d) => d.id === dispatchCenterId)?.code ?? "—";

  return (
    <div className="flex flex-col h-full">
      {/* Sticky page header — title + global save */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold leading-tight">
            Rate workspace
          </h1>
          <p className="text-xs text-muted-foreground truncate">
            What EZX charges, and what the carrier is owed — for one carrier
            service at one dispatch center.
          </p>
        </div>
      </div>

      {/* ── ONE SAVE MODEL ─────────────────────────────────────────────────
          Every value edit on this screen — rates, take rate, dim factor —
          feeds this bar and nothing else.

          It replaces a header "Save all rates" button that did NOT include the
          take rate or the dim factor: those wrote themselves, immediately, on
          their own identical-looking Save buttons. Two persistence models,
          indistinguishable on screen, on a page that sets money. You could edit
          the take rate, press the big Save, and your edit was not in it. */}
      {dirtySections.length > 0 && (
        <div className="sticky bottom-0 z-30 border-t bg-background/90 backdrop-blur px-4 sm:px-6 py-3 order-last">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              Unsaved changes in{" "}
              <strong className="text-foreground">
                {dirtySections.join(", ")}
              </strong>
              {" — nothing is written until you save."}
            </span>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardAll}
              disabled={isSaving}
            >
              Discard
            </Button>
            <Button onClick={handleSaveAll} disabled={isSaving} size="sm">
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save all changes
            </Button>
          </div>
        </div>
      )}

      {/* ⚠️ LOCKED WHILE WRITING — and this is not decoration.
          The screen stayed interactive during an async save, so a save's completion
          handler wrote into state that could, by then, hold a NEWER edit (typed
          between the click and the response) or belong to a DIFFERENT carrier
          service (the scope was switchable mid-flight). Every such write then had
          to defend itself against staleness, and every round of review found
          another one that did not.

          A native `fieldset[disabled]` disables every form control beneath it, so
          the state cannot move under the save at all. `display: contents` keeps the
          layout identical. The save bar sits OUTSIDE this, so it stays operable. */}
      <fieldset disabled={isSaving} className="contents">
        <div className="flex-1 space-y-3 p-3 sm:p-4">
          {/* A locked screen with no explanation is its own bug. If we could not read
            the rates, say so — and stay locked, because an editable screen seeded
            from a failed request is how you overwrite real rates with nothing. */}
          {loadError && (
            <div className="flex items-start gap-3 rounded-lg border border-bad/30 bg-bad-soft p-3">
              <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0 text-bad" />
              <div className="text-sm">
                <strong className="font-semibold text-bad">
                  Could not load the current pricing.
                </strong>
                <p className="text-muted-foreground mt-0.5">
                  Editing is disabled — saving now would write over rates or a
                  fee this screen never managed to read. Reload to try again.
                </p>
              </div>
            </div>
          )}

          {/* SUMMARY BEFORE DETAIL. A pricing screen that makes you assemble
            "this lane is unpriced and earns us nothing" out of three cards is
            withholding what it already knows. */}
          {scopeReady &&
            isSelfRatedCarrier === true &&
            unpricedLaneBrackets > 0 && (
              <div className="flex items-start gap-3 rounded-lg border border-warn/30 bg-warn-soft p-3">
                <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0 text-warn" />
                <div className="text-sm">
                  <strong className="font-semibold text-warn">
                    {unpricedLaneBrackets} configured lane bracket
                    {unpricedLaneBrackets === 1 ? " is" : "s are"} unpriced.
                  </strong>
                  <p className="text-muted-foreground mt-0.5">
                    A matching shipment in one of those weight brackets receives
                    no carrier option. Empty is not $0, and label rates never
                    fall back to another lane.
                  </p>
                </div>
              </div>
            )}

          {/* Scope — ONE compact sticky line. It is CONTEXT, not work: it was a
            ~230px card for three selects, which pushed the rate tables (the
            actual job) below the fold. Sticky so it stays put down the lanes. */}
          <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b bg-background/85 px-4 py-2 backdrop-blur sm:px-6">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Scope
            </span>
            <Select
              value={carrierServiceId ?? ""}
              onValueChange={(v) => setCarrierServiceId(v || null)}
            >
              <SelectTrigger className="h-8 w-[210px] text-sm">
                <SelectValue placeholder="Pick a carrier service" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Active services</SelectLabel>
                  {carrierServiceList.map((cs) => (
                    <SelectItem key={cs.id} value={cs.id}>
                      {cs.carrier.name} → {cs.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {pickupOperators.length > 0 && (
              <Select
                value={pickupProviderCarrierId ?? ""}
                onValueChange={setPickupProviderId}
              >
                <SelectTrigger
                  aria-label="Pickup operator"
                  className="h-8 w-[190px] text-sm"
                >
                  <SelectValue placeholder="Pickup operator…" />
                </SelectTrigger>
                <SelectContent>
                  {pickupOperators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {pickupServiceList.length > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <Select
                  value={pickupServiceId ?? ""}
                  onValueChange={(v) => setPickupServiceId(v || null)}
                >
                  <SelectTrigger
                    className="h-8 w-[170px] text-sm"
                    aria-label="Pickup service"
                  >
                    <SelectValue
                      placeholder={
                        pickupServicesError
                          ? "Couldn't load pickup services"
                          : "Pickup service…"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {pickupServiceList.map((ps) => (
                      <SelectItem key={ps.id} value={ps.id}>
                        {ps.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {dispatchList.length > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <Select
                  value={dispatchCenterId ?? ""}
                  onValueChange={(v) => setDispatchCenterId(v || null)}
                >
                  <SelectTrigger
                    className="h-8 w-[170px] text-sm"
                    aria-label="Dispatch center"
                  >
                    <SelectValue placeholder="Dispatch center…" />
                  </SelectTrigger>
                  <SelectContent>
                    {dispatchList.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          {workspaceCarrierId && pickupOperators.length === 0 && (
            <p role="status" className="text-sm text-muted-foreground">
              {pickupSetupError
                ? "Could not load pickup permissions. Reload to try again."
                : !pickupOptionsReady
                  ? "Loading pickup options…"
                  : "No pickup service is enabled for this shipping carrier."}{" "}
              <Link to="/add-on-services/grants" className="underline">
                Manage platform grants
              </Link>
              {" · "}
              <Link to="/add-on-services" className="underline">
                Manage pickup services and access
              </Link>
            </p>
          )}

          {pickupProviderCarrierId && dispatchList.length === 0 && (
            <p role="status" className="text-sm text-muted-foreground">
              {dispatchCentersError
                ? "Could not load dispatch centers. Reload to try again."
                : !dispatchCenters
                  ? "Loading dispatch centers…"
                  : "This pickup operator has no active dispatch center."}{" "}
              <Link to="/carriers/dispatch-centers" className="underline">
                Manage dispatch centers
              </Link>
            </p>
          )}

          {!carrierServiceId ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Pick a carrier service above to start editing rates.
              </CardContent>
            </Card>
          ) : null}

          {!carrierServiceId ? null : columns.length === 0 &&
            routes.length === 0 ? (
            <Card>
              <CardContent className="p-3">
                <EmptyState
                  onAddBracket={() => setAddBracketOpen(true)}
                  onAddRoute={() => setAddRouteOpen(true)}
                />
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Onboarding — a soft, secondary signal when a scope has no
                brackets or no lanes yet. The authoritative "can this be quoted"
                roll-up is the warn banner above; this one only names the missing
                setup step. */}
              {(columns.length === 0 || routes.length === 0) && (
                <div className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft p-3 text-sm text-warn">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {columns.length === 0
                      ? "No package weight bands yet — add one to start pricing. Brackets are the columns of the label matrix (e.g. 0–7 kg, 7–15 kg)."
                      : "No lanes yet — add an origin → destination lane. Unconfigured lanes receive no carrier option."}
                  </span>
                </div>
              )}

              {/* ── LABEL — the hero ─────────────────────────────────────────
                The label leg is the ONLY thing the pricing engine keys on a
                lane, and only for self-rated carriers. So it gets the matrix
                (lanes × brackets). An API-procured carrier prices its label
                from the provider's API — there is nothing to set per lane, so
                it collapses to a one-line notice. */}
              {isSelfRatedCarrier === false ? (
                <>
                  <LabelApiNotice
                    carrierName={carrierService?.carrier.name ?? "the carrier"}
                  />
                  {/* Label lanes don't apply to an API carrier, so the matrix
                    (which hosts bracket add/remove for self-rated) is hidden.
                    Brackets still structure this carrier's redistribution rates,
                    so bracket management gets its own bar here. */}
                  <BracketBar
                    columns={columns}
                    onAddBracket={() => setAddBracketOpen(true)}
                    onRemoveBracket={handleRemoveBracket}
                  />
                </>
              ) : isSelfRatedCarrier === true ? (
                <LabelMatrix
                  routes={routes}
                  columns={columns}
                  areas={areasList}
                  onCellChange={editCell}
                  onCellBlur={handleCellBlur}
                  onCellActivate={activateCell}
                  onCellClear={clearCell}
                  onAddBracket={() => setAddBracketOpen(true)}
                  onRemoveBracket={handleRemoveBracket}
                  onAddRoute={() => setAddRouteOpen(true)}
                  onCopyRoute={(idx) => setCopySourceIdx(idx)}
                  onDeleteRoute={handleDeleteRoute}
                />
              ) : null}

              {/* ── REDIST + PICKUP — fixed for the whole scope ──────────────
                Neither varies by lane in the pricing engine: redistribution is
                keyed on (dispatch, carrier) with no area column at all, and
                pickup keys on the origin area only. So they are compact,
                content-width cards — not lane columns repeated N times. */}
              <div className="grid items-start gap-3 sm:grid-cols-2">
                <RedistRatesSection
                  columns={columns}
                  cells={redistCells}
                  dispatchCode={dispatchCode}
                  carrierName={carrierService?.carrier.name ?? "?"}
                  handlers={{
                    onCellChange: editRedistCell,
                    onCellBlur: handleRedistCellBlur,
                    onCellActivate: activateRedistCell,
                    onCellClear: clearRedistCell,
                  }}
                />
                <PickupReferenceCard pickupServiceId={pickupServiceId} />
              </div>
            </>
          )}

          {/* ── Config, below the lanes ──────────────────────────────────
              The take rate, dim factor and quote preview are settings you tune
              AROUND the rates, not the rates themselves — so they sit under the
              tables that are the actual job, not on top of them. */}
          {/* Phase 9: markup tile — between scope and matrix. Always shown when
            a carrier service is picked (the fee applies to self-rated + API-procured alike). */}
          {carrierServiceId && carrierService && workspaceCarrierId && (
            <MarkupConfig
              ref={markupRef}
              onDirtyChange={handleMarkupDirty}
              onSavingChange={handleMarkupSaving}
              onLoadErrorChange={handleMarkupLoadError}
              carrierId={workspaceCarrierId}
              carrierName={carrierService.carrier.name}
              carrierServiceId={carrierServiceId}
              carrierServiceLabel={`${carrierService.carrier.name} → ${carrierService.name}`}
              serviceCount={
                carrierServiceList.filter(
                  (cs) => cs.carrier.id === workspaceCarrierId
                ).length
              }
            />
          )}

          {/* Service properties — editable attributes of the picked carrier
            service (currently just dim factor; future home for other small
            per-service knobs). Hidden until a service is picked. */}
          {carrierServiceId && (
            <Card>
              <CardHeader className="px-3 pt-2.5 pb-1.5">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Service properties
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 pt-0">
                <div className="flex flex-wrap items-end gap-3">
                  <div
                    className="space-y-1.5"
                    title="Dimensional weight factor: L × W × H (cm) ÷ this = dim weight kg. Used when a package's dim weight exceeds its actual weight."
                  >
                    <Label className="text-xs">Dim factor (cm³)</Label>
                    <div className="flex gap-1.5">
                      <Input
                        type="number"
                        step="1"
                        value={dimFactorDraft}
                        onChange={(e) => setDimFactorDraft(e.target.value)}
                        placeholder="(unset)"
                        className="h-9 text-sm w-32"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      L × W × H (cm) ÷ this = dim weight kg
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quote preview — runs the same engine the customer quote uses for
            a sample (lane, weight) tuple so admin can sanity-check what the
            customer actually pays against the raw band cells below. */}
          <QuotePreviewPanel
            carrierServiceId={carrierServiceId}
            pickupServiceId={pickupServiceId}
            dispatchCenterId={dispatchCenterId}
            destinationCarrierId={workspaceCarrierId}
            serviceAreas={serviceAreas ?? []}
          />
        </div>
      </fieldset>

      {/* Add bracket dialog */}
      <Dialog open={addBracketOpen} onOpenChange={setAddBracketOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add weight bracket</DialogTitle>
            <DialogDescription>
              A bracket is a package billable-weight range column (e.g., 0–7
              kg). All three rate types share these brackets so totals line up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Package billable kg from (inclusive)</Label>
              <Input
                type="number"
                step="0.01"
                value={addBracketFrom}
                onChange={(e) => setAddBracketFrom(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label>Package billable kg to (exclusive)</Label>
              <Input
                type="number"
                step="0.01"
                value={addBracketTo}
                onChange={(e) => setAddBracketTo(e.target.value)}
                placeholder="7"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBracketOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddBracket}>Add bracket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add lane dialog */}
      <Dialog open={addRouteOpen} onOpenChange={setAddRouteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add lane</DialogTitle>
            <DialogDescription>
              A lane is an origin → destination pair. It gets its own label
              rate; redistribution and pickup do not vary by lane.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>From service area (origin)</Label>
              <Select
                value={addRouteFromAreaId}
                onValueChange={setAddRouteFromAreaId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick origin area" />
                </SelectTrigger>
                <SelectContent>
                  {areasList.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.name} ({area.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>To service area (destination)</Label>
              <Select
                value={addRouteToAreaId}
                onValueChange={setAddRouteToAreaId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick destination area" />
                </SelectTrigger>
                <SelectContent>
                  {areasList.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.name} ({area.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRouteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddRoute}>Add lane</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy route dialog — pick a new (from, to) for cloned pricing. */}
      <Dialog
        open={copySourceIdx !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCopySourceIdx(null);
            setCopyFromAreaId("");
            setCopyToAreaId("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy lane pricing</DialogTitle>
          </DialogHeader>
          {copySourceIdx !== null && routes[copySourceIdx] && (
            <div className="text-xs text-muted-foreground">
              Cloning label rates from{" "}
              <RouteLabel route={routes[copySourceIdx]} areas={areasList} />.
              Pick a new origin and destination — every weight bracket's label
              cell gets duplicated. Nothing is saved until you click{" "}
              <strong>Save all changes</strong>.
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>From service area (origin)</Label>
              <Select value={copyFromAreaId} onValueChange={setCopyFromAreaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick origin area" />
                </SelectTrigger>
                <SelectContent>
                  {areasList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>To service area (destination)</Label>
              <Select value={copyToAreaId} onValueChange={setCopyToAreaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick destination area" />
                </SelectTrigger>
                <SelectContent>
                  {areasList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCopySourceIdx(null);
                setCopyFromAreaId("");
                setCopyToAreaId("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCopyRoute}>Copy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Label rate matrix: lanes (rows) × weight brackets (columns)
// ============================================================================
//
// The label leg is the ONLY leg the pricing engine keys on a lane, and only for
// self-rated carriers. So label rates are a matrix — one row per lane, one
// column per weight bracket — instead of a full-width card per lane repeating
// pickup and redistribution (which do not vary by lane). Every row is an
// explicit origin → destination pair; unmatched lanes are unavailable.

/**
 * Bracket management for API carriers. Self-rated carriers add/remove weight
 * brackets from the label matrix header; an API carrier has no matrix, but its
 * redistribution rates still need brackets — so this bar carries the same
 * add/remove actions, and (unlike the redist card) it renders even when there
 * are no brackets yet so the first one can be added.
 */
function BracketBar({
  columns,
  onAddBracket,
  onRemoveBracket,
}: {
  columns: BandColumn[];
  onAddBracket: () => void;
  onRemoveBracket: (colIdx: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2">
      <span className="text-xs font-medium text-muted-foreground">
        Package billable-weight bands
      </span>
      {columns.length === 0 ? (
        <span className="text-xs text-muted-foreground">
          none yet — add one to price redistribution.
        </span>
      ) : (
        columns.map((col, colIdx) => (
          <span
            key={`${col.weightFromKg}:${col.weightToKg}`}
            className="num inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs"
          >
            {col.weightFromKg} – {col.weightToKg} kg
            <Button
              variant="ghost"
              size="sm"
              className="-mr-1 h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => onRemoveBracket(colIdx)}
              title="Remove this bracket"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </span>
        ))
      )}
      <Button
        variant="outline"
        size="sm"
        className="ml-auto h-7 shrink-0"
        onClick={onAddBracket}
        title="Add a weight bracket"
      >
        <Plus className="h-3 w-3 mr-1" />
        Bracket
      </Button>
    </div>
  );
}

function LabelApiNotice({ carrierName }: { carrierName: string }) {
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <Badge variant="outline" className="shrink-0 font-normal">
          {carrierName} API
        </Badge>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            Nothing to configure per lane.
          </span>{" "}
          The label price is fetched live from {carrierName} at quote time, per
          parcel. There are no rate bands to edit — set redistribution and the
          take rate below.
        </p>
      </div>
    </Card>
  );
}

function LabelMatrix({
  routes,
  columns,
  areas,
  onCellChange,
  onCellBlur,
  onCellActivate,
  onCellClear,
  onAddBracket,
  onRemoveBracket,
  onAddRoute,
  onCopyRoute,
  onDeleteRoute,
}: {
  routes: UnifiedRoute[];
  columns: BandColumn[];
  areas: { id: string; code: string; name: string }[];
  onCellChange: (
    routeIdx: number,
    rateType: RateType,
    colIdx: number,
    field: keyof BandCell,
    value: number | null
  ) => void;
  onCellBlur: (
    routeIdx: number,
    rateType: RateType,
    colIdx: number,
    field: keyof BandCell
  ) => void;
  onCellActivate: (
    routeIdx: number,
    rateType: RateType,
    colIdx: number
  ) => void;
  onCellClear: (routeIdx: number, rateType: RateType, colIdx: number) => void;
  onAddBracket: () => void;
  onRemoveBracket: (colIdx: number) => void;
  onAddRoute: () => void;
  onCopyRoute: (routeIdx: number) => void;
  onDeleteRoute: (routeIdx: number) => void;
}) {
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
        <div className="mr-auto min-w-0">
          <div className="text-sm font-semibold">Label rates</div>
          <p className="text-xs text-muted-foreground">
            Package billable-weight bands · Base / shipment
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          onClick={onAddBracket}
          title="Add a weight bracket (a column of the matrix)"
        >
          <Plus className="h-3 w-3 mr-1" />
          Bracket
        </Button>
      </div>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table className="[&_td]:px-4 [&_th]:px-4">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[220px] align-bottom py-2">
                  Lane
                </TableHead>
                {columns.map((col, colIdx) => (
                  <TableHead
                    key={`${col.weightFromKg}:${col.weightToKg}`}
                    className="group align-bottom py-2 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      <span className="num">
                        {col.weightFromKg} – {col.weightToKg} kg
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => onRemoveBracket(colIdx)}
                        title="Remove this bracket from every lane"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableHead>
                ))}
                <TableHead className="w-[76px] py-2" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {routes.map((route, routeIdx) => {
                // Label-only gaps: an empty cell leaves that exact
                // lane/bracket unavailable. Redistribution is workspace-wide
                // and owns its own gap signal.
                const gaps = route.customerCells.filter(
                  (c) => c.isEmpty
                ).length;
                return (
                  <TableRow key={route.key} className="hover:bg-transparent">
                    <TableCell className="py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <RouteLabel route={route} areas={areas} />
                        {gaps > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge className="h-5 rounded border-warn/30 bg-warn-soft px-1.5 text-[11px] font-medium text-warn hover:bg-warn-soft">
                              {gaps} unpriced
                            </Badge>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {columns.map((col, colIdx) => (
                      <TableCell
                        key={`${col.weightFromKg}:${col.weightToKg}`}
                        className="py-2 align-top"
                      >
                        <BandCellEditor
                          cell={route.customerCells[colIdx]}
                          column={col}
                          onChange={(field, value) =>
                            onCellChange(
                              routeIdx,
                              "customer",
                              colIdx,
                              field,
                              value
                            )
                          }
                          onBlur={(field) =>
                            onCellBlur(routeIdx, "customer", colIdx, field)
                          }
                          onActivate={() =>
                            onCellActivate(routeIdx, "customer", colIdx)
                          }
                          onClear={() =>
                            onCellClear(routeIdx, "customer", colIdx)
                          }
                        />
                      </TableCell>
                    ))}
                    <TableCell className="py-2 align-top">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => onCopyRoute(routeIdx)}
                          title="Copy this lane's rates to a new (origin → destination) pair"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => onDeleteRoute(routeIdx)}
                          title="Remove this lane"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <div className="border-t px-2 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-primary hover:text-primary"
          onClick={onAddRoute}
          title="Add a lane (origin → destination)"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add lane
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// Pickup reference — pickup keys on the origin area, edited on its own screen
// ============================================================================

function PickupReferenceCard({
  pickupServiceId,
}: {
  pickupServiceId: string | null;
}) {
  return (
    <Card>
      <div className="border-b px-4 py-2.5">
        <div className="text-sm font-semibold">Pickup</div>
        <p className="text-xs text-muted-foreground">
          Sender → dispatch center. Keyed on the origin area, not the lane.
        </p>
      </div>
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <p className="text-xs text-muted-foreground">
          Pickup rates are set for all carriers at this dispatch center, on
          their own screen.
        </p>
        <Button asChild variant="outline" size="sm" className="h-8 shrink-0">
          <Link
            to={
              pickupServiceId
                ? `/add-on-services/pickup-rates?serviceId=${pickupServiceId}`
                : "/add-on-services/pickup-rates"
            }
          >
            Edit pickup rates
            <ExternalLink className="h-3 w-3 ml-1" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Empty-state walkthrough
// ============================================================================

function EmptyState({
  onAddBracket,
  onAddRoute,
}: {
  onAddBracket: () => void;
  onAddRoute: () => void;
}) {
  return (
    <div className="py-8 text-sm text-muted-foreground space-y-4">
      <div className="text-center font-medium text-foreground">
        No rates yet for this scope.
      </div>
      <div className="max-w-[640px] mx-auto space-y-3">
        <div className="flex items-start gap-3">
          <Badge variant="outline" className="mt-0.5">
            1
          </Badge>
          <div>
            <strong className="text-foreground">Add weight brackets</strong>
            {" — "}the columns of the label matrix. Common shape: 0–7 kg, 7–15
            kg, 15+ kg. Brackets are shared with the redistribution rates.
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Badge variant="outline" className="mt-0.5">
            2
          </Badge>
          <div>
            <strong className="text-foreground">Add lanes</strong>
            {" — "}origin → destination pairs. Only configured lanes can return
            this carrier as a quote option.
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Badge variant="outline" className="mt-0.5">
            3
          </Badge>
          <div>
            <strong className="text-foreground">Fill in the cells</strong>
            {" — "}each cell holds a flat base + optional per-kg slope. Bands
            are priced independently — leaving a lane's cell empty means no
            option for that lane and weight bracket.
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Badge variant="outline" className="mt-0.5">
            4
          </Badge>
          <div>
            <strong className="text-foreground">Save all changes</strong>
            {" — "}one bar at the bottom commits the label and redistribution
            rates together. Pickup rates are edited on their own screen.
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onAddBracket}>
          <Plus className="h-3 w-3 mr-1" />
          Add weight bracket
        </Button>
        <Button variant="outline" size="sm" onClick={onAddRoute}>
          <Plus className="h-3 w-3 mr-1" />
          Add lane
        </Button>
      </div>
    </div>
  );
}
